import {
	CLUSTER_CODE_PATTERN,
	DRIVE_MINUTES_MAX,
	DRIVE_MINUTES_MIN,
	E164_PHONE_PATTERN,
	ISODOW_MAX,
	ISODOW_MIN,
	TRANSPORT_EMAIL_MAX,
	TRANSPORT_NAME_MAX,
	TRANSPORT_REMARKS_MAX,
	VEHICLE_MODEL_MAX,
	VEHICLE_PLATE_MAX,
	VEHICLE_PLATE_PATTERN,
	VEHICLE_SEATS,
	VEHICLE_TYPES,
	type VehicleStatus,
	type VehicleType,
} from "@seagames/contracts";
import type { ClusterDriveMinutes, CreateDriverBody, CreateTransportProviderBody, Driver, TransportProvider, Vehicle } from "@seagames/contracts";

/**
 * The fleet sheets' drafts and checks — pure, so they are unit-tested
 * without a DOM. A check returns message KEYS (`transportFleet:validation.*`)
 * with their values; the sheet translates them. The API re-checks
 * everything (zod + CHECKs + the upcoming-trips rule); these only keep the
 * form from sending a refusal it can predict.
 */

export interface Problem {
	key: string;
	values?: Record<string, string | number>;
	/** Already translated (a contract schema's localized message): shown as is. */
	text?: string;
}
export type Problems<F extends string> = Partial<Record<F, Problem>>;

const cleanText = (v: string): string => v.trim().replace(/\s+/g, " ");
const orNull = (v: string): string | null => (cleanText(v) === "" ? null : cleanText(v));
const isWhole = (v: string): boolean => /^\d+$/.test(v.trim());

/** "wxy  1234 " → "WXY 1234" (what the plate column stores; the unique index strips the rest). */
export const normalizePlateInput = (v: string): string => cleanText(v).toUpperCase();

// ── Vehicle ─────────────────────────────────────────────────────────────────

export interface VehicleDraft {
	vehicle_type: VehicleType;
	plate: string;
	model: string;
	cluster_id: string;
	provider_id: string;
	seats: string;
	status: VehicleStatus;
	has_ramp: boolean;
	remarks: string;
}
export type VehicleField = keyof VehicleDraft;

export const emptyVehicleDraft = (): VehicleDraft => ({
	vehicle_type: "VAN",
	plate: "",
	model: "",
	cluster_id: "",
	provider_id: "",
	seats: "",
	status: "ACTIVE",
	has_ramp: false,
	remarks: "",
});

export const vehicleDraftFrom = (v: Vehicle): VehicleDraft => ({
	vehicle_type: v.vehicle_type,
	plate: v.plate,
	model: v.model,
	cluster_id: v.cluster.cluster_id,
	provider_id: v.provider?.transport_provider_id ?? "",
	seats: String(v.seats),
	status: v.status,
	has_ramp: v.has_ramp,
	remarks: v.remarks ?? "",
});

export function checkVehicle(d: VehicleDraft): Problems<VehicleField> {
	const p: Problems<VehicleField> = {};
	if (!(VEHICLE_TYPES as readonly string[]).includes(d.vehicle_type)) p.vehicle_type = { key: "vehicleTypeRequired" };
	const plate = normalizePlateInput(d.plate);
	if (!plate) p.plate = { key: "plateRequired" };
	else if (plate.length > VEHICLE_PLATE_MAX || !VEHICLE_PLATE_PATTERN.test(plate)) p.plate = { key: "plateInvalid" };
	const model = cleanText(d.model);
	if (!model) p.model = { key: "modelRequired" };
	else if (model.length > VEHICLE_MODEL_MAX) p.model = { key: "tooLong", values: { max: VEHICLE_MODEL_MAX } };
	if (!d.cluster_id) p.cluster_id = { key: "clusterRequired" };
	const range = VEHICLE_SEATS[d.vehicle_type] ?? VEHICLE_SEATS.VAN;
	const seats = Number(d.seats);
	if (!isWhole(d.seats) || seats < range.min || seats > range.max) p.seats = { key: `seatsRange.${d.vehicle_type}`, values: { min: range.min, max: range.max } };
	if (cleanText(d.remarks).length > TRANSPORT_REMARKS_MAX) p.remarks = { key: "tooLong", values: { max: TRANSPORT_REMARKS_MAX } };
	return p;
}

/** Every vehicle field as the wire names it (create sends all of them; a PATCH the changed ones). */
export interface VehicleWire {
	vehicle_type: VehicleType;
	plate: string;
	model: string;
	seats: number;
	has_ramp: boolean;
	status: VehicleStatus;
	cluster_id: string;
	provider_id: string | null;
	remarks: string | null;
}

export const vehicleBody = (d: VehicleDraft): VehicleWire => ({
	vehicle_type: d.vehicle_type,
	plate: normalizePlateInput(d.plate),
	model: cleanText(d.model),
	seats: Number(d.seats),
	has_ramp: d.has_ramp,
	status: d.status,
	cluster_id: d.cluster_id,
	provider_id: d.provider_id || null,
	remarks: orNull(d.remarks),
});

/** Only what changed — every field sent is an audit line. */
export function vehiclePatch(d: VehicleDraft, before: Vehicle): Partial<VehicleWire> {
	const next = vehicleBody(d);
	const was = vehicleBody(vehicleDraftFrom(before));
	return diff(next, was);
}

// ── Driver ──────────────────────────────────────────────────────────────────

export interface DriverDraft {
	name: string;
	dial: string;
	number: string;
	cluster_id: string;
	provider_id: string;
	vehicle_types: VehicleType[];
	vehicle_ids: string[];
	shift_start: string;
	shift_end: string;
	/** `""` = no fixed rest day, else "1".."7". */
	rest_isodow: string;
	is_active: boolean;
}
export type DriverField = keyof DriverDraft;

export const emptyDriverDraft = (): DriverDraft => ({
	name: "",
	dial: "60",
	number: "",
	cluster_id: "",
	provider_id: "",
	vehicle_types: [],
	vehicle_ids: [],
	shift_start: "07:00",
	shift_end: "19:00",
	rest_isodow: "",
	is_active: true,
});

export function driverDraftFrom(d: Driver, callingCodes: readonly string[]): DriverDraft {
	const [dial, number] = splitE164(d.phone, callingCodes);
	return {
		name: d.name,
		dial,
		number,
		cluster_id: d.cluster.cluster_id,
		provider_id: d.provider?.transport_provider_id ?? "",
		vehicle_types: VEHICLE_TYPES.filter((t) => d.vehicle_types.includes(t)),
		vehicle_ids: d.usual_vehicles.map((v) => v.vehicle_id),
		shift_start: d.shift_start.slice(0, 5),
		shift_end: d.shift_end.slice(0, 5),
		rest_isodow: d.rest_isodow === null ? "" : String(d.rest_isodow),
		is_active: d.is_active,
	};
}

/** `+` + calling code + the digits typed (spaces and dashes dropped). */
export const composePhone = (dial: string, number: string): string => `+${dial.replace(/\D/g, "")}${number.replace(/\D/g, "")}`;

/**
 * Her rules (`saveDriver`) minus one: name, a phone of 7–12 digits, a
 * cluster, at least one vehicle type, a shift whose start and end differ.
 * Usual vehicles are OPTIONAL (hers required one per ticked type, which
 * blocked every edit of a driver certified for more types than they
 * usually drive — E2E B5); the ones chosen must fit a ticked type.
 * `vehicleTypeOf` answers for the vehicles the sheet has loaded.
 */
export function checkDriver(d: DriverDraft, vehicleTypeOf: (vehicleId: string) => VehicleType | undefined): Problems<DriverField> {
	const p: Problems<DriverField> = {};
	const name = cleanText(d.name);
	if (!name) p.name = { key: "driverNameRequired" };
	else if (name.length > TRANSPORT_NAME_MAX) p.name = { key: "tooLong", values: { max: TRANSPORT_NAME_MAX } };
	const digits = d.number.replace(/[\s-]/g, "");
	if (!d.dial) p.number = { key: "dialRequired" };
	else if (!/^\d{7,12}$/.test(digits)) p.number = { key: "phoneDigits", values: { min: 7, max: 12 } };
	else if (!E164_PHONE_PATTERN.test(composePhone(d.dial, digits))) p.number = { key: "phoneInvalid" };
	if (!d.cluster_id) p.cluster_id = { key: "clusterRequired" };
	if (d.vehicle_types.length === 0) p.vehicle_types = { key: "typesRequired" };
	// Usual vehicles are optional (E2E B5): only the ones chosen must be of a certified type.
	// (Same cluster: the picker lists only the cluster's vehicles; the API re-checks both.)
	else if (d.vehicle_ids.some((id) => {
		const type = vehicleTypeOf(id);
		return type !== undefined && !d.vehicle_types.includes(type);
	}))
		p.vehicle_ids = { key: "vehicleNotCertified" };
	if (!/^\d{2}:\d{2}$/.test(d.shift_start)) p.shift_start = { key: "timeRequired" };
	if (!/^\d{2}:\d{2}$/.test(d.shift_end)) p.shift_end = { key: "timeRequired" };
	else if (d.shift_end === d.shift_start) p.shift_end = { key: "shiftSame" };
	if (d.rest_isodow !== "") {
		const n = Number(d.rest_isodow);
		if (!Number.isInteger(n) || n < ISODOW_MIN || n > ISODOW_MAX) p.rest_isodow = { key: "invalidValue" };
	}
	return p;
}

export const driverBody = (d: DriverDraft): CreateDriverBody => ({
	name: cleanText(d.name).toUpperCase(),
	phone: composePhone(d.dial, d.number),
	cluster_id: d.cluster_id,
	provider_id: d.provider_id || null,
	vehicle_types: VEHICLE_TYPES.filter((t) => d.vehicle_types.includes(t)),
	vehicle_ids: [...d.vehicle_ids].sort(),
	shift_start: d.shift_start,
	shift_end: d.shift_end,
	rest_isodow: d.rest_isodow === "" ? null : Number(d.rest_isodow),
	is_active: d.is_active,
});

export function driverPatch(d: DriverDraft, before: Driver, callingCodes: readonly string[]): Partial<CreateDriverBody> {
	return diff(driverBody(d), driverBody(driverDraftFrom(before, callingCodes)));
}

/**
 * Untick a type or change the cluster: the usual vehicles that no longer
 * fit are dropped (hers cleared them all on a cluster change and said so).
 */
export function keepFittingVehicles(ids: readonly string[], types: readonly VehicleType[], vehicleTypeOf: (id: string) => VehicleType | undefined): string[] {
	return ids.filter((id) => {
		const t = vehicleTypeOf(id);
		return t !== undefined && types.includes(t);
	});
}

// ── Provider ────────────────────────────────────────────────────────────────

export interface ProviderDraft {
	name: string;
	contact_name: string;
	dial: string;
	number: string;
	email: string;
	is_active: boolean;
}
export type ProviderField = keyof ProviderDraft;

export const emptyProviderDraft = (): ProviderDraft => ({ name: "", contact_name: "", dial: "60", number: "", email: "", is_active: true });

export function providerDraftFrom(p: TransportProvider, callingCodes: readonly string[]): ProviderDraft {
	const [dial, number] = p.contact_phone ? splitE164(p.contact_phone, callingCodes) : ["60", ""];
	return { name: p.name, contact_name: p.contact_name ?? "", dial: dial || "60", number, email: p.email ?? "", is_active: p.is_active };
}

export function checkProvider(d: ProviderDraft): Problems<ProviderField> {
	const p: Problems<ProviderField> = {};
	const name = cleanText(d.name);
	if (!name) p.name = { key: "companyNameRequired" };
	else if (name.length > TRANSPORT_NAME_MAX) p.name = { key: "tooLong", values: { max: TRANSPORT_NAME_MAX } };
	if (cleanText(d.contact_name).length > TRANSPORT_NAME_MAX) p.contact_name = { key: "tooLong", values: { max: TRANSPORT_NAME_MAX } };
	const digits = d.number.replace(/[\s-]/g, "");
	if (digits !== "" && !/^\d{7,12}$/.test(digits)) p.number = { key: "phoneDigits", values: { min: 7, max: 12 } };
	const email = d.email.trim();
	if (email !== "" && (email.length > TRANSPORT_EMAIL_MAX || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) p.email = { key: "emailInvalid" };
	return p;
}

export const providerBody = (d: ProviderDraft): CreateTransportProviderBody => {
	const digits = d.number.replace(/[\s-]/g, "");
	return {
		name: cleanText(d.name),
		contact_name: orNull(d.contact_name),
		contact_phone: digits ? composePhone(d.dial, digits) : null,
		email: d.email.trim() === "" ? null : d.email.trim().toLowerCase(),
		is_active: d.is_active,
	};
};

// ── Clusters ────────────────────────────────────────────────────────────────

/** "kl " → "KL". */
export const normalizeClusterCode = (v: string): string => v.trim().toUpperCase();

export function checkCluster(d: { code: string; name: string }): Problems<"code" | "name"> {
	const p: Problems<"code" | "name"> = {};
	if (!CLUSTER_CODE_PATTERN.test(normalizeClusterCode(d.code))) p.code = { key: "clusterCode" };
	if (!cleanText(d.name)) p.name = { key: "clusterNameRequired" };
	return p;
}

// ── Drive-time matrix ───────────────────────────────────────────────────────

/** `from|to` → minutes, as typed. An empty cell = the games default. */
export type MatrixDraft = Record<string, string>;
export const cellKey = (from: string, to: string): string => `${from}|${to}`;

export function matrixDraftFrom(rows: readonly ClusterDriveMinutes[]): MatrixDraft {
	const m: MatrixDraft = {};
	for (const r of rows) m[cellKey(r.from_cluster_id, r.to_cluster_id)] = String(r.minutes);
	return m;
}

/**
 * Type into one cell. With `mirror` (the default: drive times are usually the
 * same both ways) the opposite cell follows — unless it already held a
 * different number, which means the user split the pair on purpose. The
 * diagonal (a trip inside one cluster) is a cell like any other, with no
 * mirror: the seed and the desk set it (KL→KL 20 min), and a save must never
 * drop it (E2E B4).
 */
export function setCell(m: MatrixDraft, from: string, to: string, value: string, mirror: boolean): MatrixDraft {
	const next = { ...m, [cellKey(from, to)]: value };
	if (mirror && from !== to) {
		const back = cellKey(to, from);
		const before = m[cellKey(from, to)] ?? "";
		if ((m[back] ?? "") === before) next[back] = value;
	}
	return next;
}

/** The cells that are not whole minutes in range (empty is fine). */
export function badCells(m: MatrixDraft): string[] {
	return Object.entries(m)
		.filter(([, v]) => v.trim() !== "" && (!isWhole(v) || Number(v) < DRIVE_MINUTES_MIN || Number(v) > DRIVE_MINUTES_MAX))
		.map(([k]) => k);
}

/** The replace-set body: every filled cell (the diagonal included) between clusters that still exist, sorted (a stable audit). */
export function matrixRows(m: MatrixDraft, clusterIds: readonly string[]): ClusterDriveMinutes[] {
	const known = new Set(clusterIds);
	return Object.entries(m)
		.filter(([, v]) => v.trim() !== "")
		.map(([k, v]) => {
			const [from = "", to = ""] = k.split("|");
			return { from_cluster_id: from, to_cluster_id: to, minutes: Number(v) };
		})
		.filter((r) => known.has(r.from_cluster_id) && known.has(r.to_cluster_id))
		.sort((a, b) => a.from_cluster_id.localeCompare(b.from_cluster_id) || a.to_cluster_id.localeCompare(b.to_cluster_id));
}

/** Same rows, same minutes — nothing to save. */
export function sameMatrix(a: readonly ClusterDriveMinutes[], b: readonly ClusterDriveMinutes[]): boolean {
	const key = (r: ClusterDriveMinutes) => `${r.from_cluster_id}|${r.to_cluster_id}|${r.minutes}`;
	const sa = a.map(key).sort();
	const sb = b.map(key).sort();
	return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

// ── Weeks (driver schedule) ─────────────────────────────────────────────────

const DAY_MS = 86_400_000;
const utc = (ymd: string) => Date.parse(`${ymd}T00:00:00Z`);
export const addDaysYmd = (ymd: string, n: number): string => new Date(utc(ymd) + n * DAY_MS).toISOString().slice(0, 10);

/** The Monday on or before `ymd` (weeks start on Monday, as hers). */
export function mondayOf(ymd: string): string {
	const dow = new Date(utc(ymd)).getUTCDay(); // 0 = Sunday
	return addDaysYmd(ymd, -((dow + 6) % 7));
}

/** ISO weekday of a calendar date: 1 = Monday … 7 = Sunday. */
export const isoDow = (ymd: string): number => ((new Date(utc(ymd)).getUTCDay() + 6) % 7) + 1;

// ── Phone ───────────────────────────────────────────────────────────────────

/**
 * An E.164 number split at its calling code, using the countries' calling
 * codes (data, `/countries/options`): `+60123456789` → `["60", "123456789"]`.
 * The longest matching code wins (+673 before +6…). Unknown → `["", digits]`.
 */
export function splitE164(phone: string, callingCodes: readonly string[]): [dial: string, number: string] {
	const digits = phone.replace(/^\+/, "");
	let best = "";
	for (const code of callingCodes) if (code.length > best.length && digits.startsWith(code)) best = code;
	return [best, digits.slice(best.length)];
}

/** `+60 123456789` for reading; the raw E.164 when the code is unknown. */
export function formatPhone(phone: string, callingCodes: readonly string[]): string {
	const [dial, rest] = splitE164(phone, callingCodes);
	return dial ? `+${dial} ${rest}` : phone;
}

// ── Shared ──────────────────────────────────────────────────────────────────

function diff<T extends object>(next: T, was: T): Partial<T> {
	const out: Partial<T> = {};
	for (const k of Object.keys(next) as (keyof T)[]) {
		if (JSON.stringify(next[k]) !== JSON.stringify(was[k])) out[k] = next[k];
	}
	return out;
}

export const hasProblems = (p: object): boolean => Object.values(p).some((v) => v !== undefined);

/**
 * A contract schema's issues (built with translated messages) → the first
 * problem per field this sheet renders; an issue on no known field goes to
 * `fallback` so it is never silently dropped.
 */
export function issuesToProblems<F extends string>(
	issues: ReadonlyArray<{ path: ReadonlyArray<string | number>; message: string }>,
	known: readonly F[],
	fallback: F,
	rename: Partial<Record<string, F>> = {},
): Problems<F> {
	const out: Problems<F> = {};
	for (const issue of issues) {
		const head = String(issue.path[0] ?? "");
		const field = (rename[head] ?? ((known as readonly string[]).includes(head) ? head : fallback)) as F;
		if (!out[field]) out[field] = { key: "invalidValue", text: issue.message };
	}
	return out;
}
